import React, { useState, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import './InfoRequestModal.css';
import { submitFranchiseRequest } from '../services/franchiseService.ts';
import analyticsService from '../../services/AnalyticsService.ts';

/**
 * Parse phone number into numerical and formatted versions
 */
const parsePhone = (phone: string) => {
  let number = phone.replace(/\D/g, '');
  if (number.length > 10) {
    number = number.substring(0, 10);
  }

  let formatted = '';
  if (number.length <= 3) {
    formatted = number;
  } else if (number.length > 3 && number.length <= 6) {
    formatted = `(${number.substring(0, 3)}) ${number.substring(3)}`;
  } else if (number.length > 6) {
    formatted = `(${number.substring(0, 3)}) ${number.substring(3, 6)}-${number.substring(6, 10)}`;
  }

  return { number, formatted };
};

const stateValues = [
  { name: 'Alabama', abbr: 'AL' },
  { name: 'Alaska', abbr: 'AK' },
  { name: 'Arizona', abbr: 'AZ' },
  { name: 'Arkansas', abbr: 'AR' },
  { name: 'California', abbr: 'CA' },
  { name: 'Colorado', abbr: 'CO' },
  { name: 'Connecticut', abbr: 'CT' },
  { name: 'Delaware', abbr: 'DE' },
  { name: 'District of Columbia', abbr: 'DC' },
  { name: 'Florida', abbr: 'FL' },
  { name: 'Georgia', abbr: 'GA' },
  { name: 'Hawaii', abbr: 'HI' },
  { name: 'Idaho', abbr: 'ID' },
  { name: 'Illinois', abbr: 'IL' },
  { name: 'Indiana', abbr: 'IN' },
  { name: 'Iowa', abbr: 'IA' },
  { name: 'Kansas', abbr: 'KS' },
  { name: 'Kentucky', abbr: 'KY' },
  { name: 'Louisiana', abbr: 'LA' },
  { name: 'Maine', abbr: 'ME' },
  { name: 'Maryland', abbr: 'MD' },
  { name: 'Massachusetts', abbr: 'MA' },
  { name: 'Michigan', abbr: 'MI' },
  { name: 'Minnesota', abbr: 'MN' },
  { name: 'Mississippi', abbr: 'MS' },
  { name: 'Missouri', abbr: 'MO' },
  { name: 'Montana', abbr: 'MT' },
  { name: 'Nebraska', abbr: 'NE' },
  { name: 'Nevada', abbr: 'NV' },
  { name: 'New Hampshire', abbr: 'NH' },
  { name: 'New Jersey', abbr: 'NJ' },
  { name: 'New Mexico', abbr: 'NM' },
  { name: 'New York', abbr: 'NY' },
  { name: 'North Carolina', abbr: 'NC' },
  { name: 'North Dakota', abbr: 'ND' },
  { name: 'Ohio', abbr: 'OH' },
  { name: 'Oklahoma', abbr: 'OK' },
  { name: 'Oregon', abbr: 'OR' },
  { name: 'Pennsylvania', abbr: 'PA' },
  { name: 'Rhode Island', abbr: 'RI' },
  { name: 'South Carolina', abbr: 'SC' },
  { name: 'South Dakota', abbr: 'SD' },
  { name: 'Tennessee', abbr: 'TN' },
  { name: 'Texas', abbr: 'TX' },
  { name: 'Utah', abbr: 'UT' },
  { name: 'Vermont', abbr: 'VT' },
  { name: 'Virginia', abbr: 'VA' },
  { name: 'Washington', abbr: 'WA' },
  { name: 'West Virginia', abbr: 'WV' },
  { name: 'Wisconsin', abbr: 'WI' },
  { name: 'Wyoming', abbr: 'WY' },
];

interface InfoRequestModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const defaultFormData = {
  firstName: '',
  lastName: '',
  email: '',
  streetAddress: 'N/A',
  city: 'N/A',
  state: '',
  netWorth: '',
  availableCash: '',
  desiredLocation: '',
  phone: { number: '', formatted: '' },
  availability: '',
  referral: '',
};

const InfoRequestModal: React.FC<InfoRequestModalProps> = ({ isOpen, onClose }) => {
  const [formData, setFormData] = useState(defaultFormData);
  const [processing, setProcessing] = useState(false);
  const [animate, setAnimate] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  // Handle body scroll lock — position: fixed prevents iOS background scroll
  useEffect(() => {
    if (isOpen) {
      const scrollY = window.scrollY;
      document.body.classList.add('info-request-modal-open');
      document.body.style.top = `-${scrollY}px`;
      // Small delay to trigger CSS animation
      const timer = setTimeout(() => setAnimate(true), 10);
      return () => clearTimeout(timer);
    } else {
      setAnimate(false);
      const scrollY = document.body.style.top;
      document.body.classList.remove('info-request-modal-open');
      document.body.style.top = '';
      window.scrollTo(0, parseInt(scrollY || '0', 10) * -1);
    }

    return () => {
      const scrollY = document.body.style.top;
      document.body.classList.remove('info-request-modal-open');
      document.body.style.top = '';
      window.scrollTo(0, parseInt(scrollY || '0', 10) * -1);
    };
  }, [isOpen]);

  // Handle escape key
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) {
        onClose();
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen, onClose]);

  const updateFormData = useCallback((key: string, value: any) => {
    setFormData(prev => ({ ...prev, [key]: value }));
  }, []);

  const handlePhoneInput = useCallback((value: string) => {
    const phone = parsePhone(value);
    updateFormData('phone', phone);
  }, [updateFormData]);

  const handleClose = useCallback(() => {
    if (!processing) {
      if (submitStatus === 'success') {
        setFormData(defaultFormData);
      }
      setSubmitStatus('idle');
      onClose();
    }
  }, [processing, submitStatus, onClose]);

  const handleBackdropClick = useCallback((e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      handleClose();
    }
  }, [handleClose]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const firstName = formData.firstName.trim();
    const lastName = formData.lastName.trim();
    const email = formData.email.trim();
    const streetAddress = formData.streetAddress.trim();
    const city = formData.city.trim();
    const { state } = formData;
    const netWorth = formData.netWorth.trim();
    const availableCash = formData.availableCash.trim();
    const desiredLocation = formData.desiredLocation.trim();
    const phone = formData.phone.number;
    const availability = formData.availability.trim();
    const referral = formData.referral.trim();

    // Validate required fields
    if (
      !firstName ||
      !lastName ||
      !email ||
      !state ||
      !availableCash ||
      !desiredLocation ||
      !phone ||
      !availability ||
      !referral
    ) {
      const missingFields = [];
      if (!firstName) missingFields.push('firstName');
      if (!lastName) missingFields.push('lastName');
      if (!email) missingFields.push('email');
      if (!state) missingFields.push('state');
      if (!availableCash) missingFields.push('availableCash');
      if (!desiredLocation) missingFields.push('desiredLocation');
      if (!phone) missingFields.push('phone');
      if (!availability) missingFields.push('availability');
      if (!referral) missingFields.push('referral');

      if (missingFields.length > 0) {
        analyticsService.handleFormValidationError(
          'franchise_info_request',
          missingFields[0],
          'Please complete all form fields.',
          'required',
          undefined,
          1
        );
      }

      window.alert('Please complete all form fields.');
      return;
    }

    setProcessing(true);

    // Track form submission attempt
    analyticsService.trackEvent('begin_checkout', {
      cart_total_items: 1,
      cart_total_value: 0,
      checkout_method: 'franchise_form',
      page_path: window.location.pathname,
    });

    try {
      await submitFranchiseRequest({
        firstName,
        lastName,
        email,
        streetAddress,
        city,
        state,
        netWorth: netWorth || 'N/A',
        availableCash,
        desiredLocation,
        phone,
        availability,
        referral,
      });

      // Track successful submission
      analyticsService.trackEvent('purchase', {
        transaction_id: `franchise_${Date.now()}`,
        value: 0,
        currency: 'USD',
        items: [
          {
            item_id: 'franchise_info_request',
            item_name: 'Franchise Information Request',
            item_category: 'franchise',
            quantity: 1,
            price: 0,
          },
        ],
        page_path: window.location.pathname,
      });

      // Set user properties
      analyticsService.setUserProperties(
        {
          franchise_interest: 'true',
          franchise_form_completed: new Date().toISOString(),
          interested_location: desiredLocation,
          available_cash_range: availableCash,
        },
        'custom'
      );

      setProcessing(false);
      setSubmitStatus('success');
    } catch (error) {
      console.error('Failed to submit info request form:', error);
      setProcessing(false);
      setSubmitStatus('error');
    }
  };

  if (!isOpen) return null;

  return createPortal(
    <>
      {/* Backdrop */}
      <div
        className={`info-request-modal__backdrop ${animate ? 'open' : ''}`}
        onClick={handleBackdropClick}
      />

      {/* Modal */}
      <div className={`info-request-modal ${animate ? 'open' : ''}`}>
        {/* Header */}
        <div className="info-request-modal__header">
          <h2 className="info-request-modal__title">Request Information</h2>
          <button
            className="info-request-modal__close"
            onClick={handleClose}
            aria-label="Close modal"
            disabled={processing}
          >
            <svg viewBox="0 0 24 24" fill="none" strokeWidth="2" strokeLinecap="round">
              <path d="M18 6L6 18M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Content */}
        <div className="info-request-modal__content">
          {submitStatus === 'idle' ? (
            <>
              <p className="info-request-modal__description">
                Please complete this form and we will get in touch to discuss our franchise offering in
                more detail.
              </p>

              <form className="info-request-modal__form" onSubmit={handleSubmit}>
                {/* Name row */}
                <div className="info-request-modal__row">
                  <div className="info-request-modal__field">
                    <input
                      type="text"
                      className="info-request-modal__input"
                      placeholder="First Name *"
                      value={formData.firstName}
                      onChange={e => updateFormData('firstName', e.target.value)}
                      disabled={processing}
                    />
                  </div>
                  <div className="info-request-modal__field">
                    <input
                      type="text"
                      className="info-request-modal__input"
                      placeholder="Last Name *"
                      value={formData.lastName}
                      onChange={e => updateFormData('lastName', e.target.value)}
                      disabled={processing}
                    />
                  </div>
                </div>

                {/* Email */}
                <div className="info-request-modal__field">
                  <input
                    type="email"
                    className="info-request-modal__input"
                    placeholder="Email *"
                    value={formData.email}
                    onChange={e => updateFormData('email', e.target.value)}
                    disabled={processing}
                  />
                </div>

                {/* Phone */}
                <div className="info-request-modal__field">
                  <input
                    type="tel"
                    className="info-request-modal__input"
                    placeholder="Best Phone # to Reach You *"
                    value={formData.phone.formatted}
                    onChange={e => handlePhoneInput(e.target.value)}
                    disabled={processing}
                  />
                </div>

                {/* State dropdown */}
                <div className="info-request-modal__field">
                  <select
                    className="info-request-modal__select"
                    value={formData.state}
                    onChange={e => updateFormData('state', e.target.value)}
                    disabled={processing}
                  >
                    <option value="">Which State Do You Reside In? *</option>
                    {stateValues.map(state => (
                      <option key={state.abbr} value={state.name}>
                        {state.name}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Net worth (optional) */}
                <div className="info-request-modal__field">
                  <input
                    type="text"
                    className="info-request-modal__input"
                    placeholder="Current Estimated Net Worth (optional)"
                    value={formData.netWorth}
                    onChange={e => updateFormData('netWorth', e.target.value)}
                    disabled={processing}
                  />
                </div>

                {/* Available cash */}
                <div className="info-request-modal__field">
                  <input
                    type="text"
                    className="info-request-modal__input"
                    placeholder="Cash Available for Investment *"
                    value={formData.availableCash}
                    onChange={e => updateFormData('availableCash', e.target.value)}
                    disabled={processing}
                  />
                </div>

                {/* Desired location */}
                <div className="info-request-modal__field">
                  <input
                    type="text"
                    className="info-request-modal__input"
                    placeholder="State/City of Interest *"
                    value={formData.desiredLocation}
                    onChange={e => updateFormData('desiredLocation', e.target.value)}
                    disabled={processing}
                  />
                </div>

                {/* Availability */}
                <div className="info-request-modal__field">
                  <input
                    type="text"
                    className="info-request-modal__input"
                    placeholder="Best Time to Call *"
                    value={formData.availability}
                    onChange={e => updateFormData('availability', e.target.value)}
                    disabled={processing}
                  />
                </div>

                {/* Referral */}
                <div className="info-request-modal__field">
                  <input
                    type="text"
                    className="info-request-modal__input"
                    placeholder="How Did You Hear About Us? *"
                    value={formData.referral}
                    onChange={e => updateFormData('referral', e.target.value)}
                    disabled={processing}
                  />
                </div>

                {/* Submit button */}
                <button
                  type="submit"
                  className="info-request-modal__submit"
                  disabled={processing}
                >
                  {processing ? <span className="info-request-modal__spinner" /> : 'Submit'}
                </button>
              </form>
            </>
          ) : (
            <div className="info-request-modal__confirmation">
              {submitStatus === 'success' ? (
                <>
                  <div className="info-request-modal__confirmation-icon">
                    <svg viewBox="0 0 24 24" fill="none" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M20 6L9 17l-5-5" />
                    </svg>
                  </div>
                  <h3 className="info-request-modal__confirmation-heading">Thank You!</h3>
                  <p className="info-request-modal__confirmation-text">
                    Your request has been submitted successfully. We will be in touch soon to discuss our franchise offering.
                  </p>
                  <button className="info-request-modal__submit" onClick={handleClose}>Close</button>
                </>
              ) : (
                <>
                  <div className="info-request-modal__confirmation-icon info-request-modal__confirmation-icon--error">
                    <svg viewBox="0 0 24 24" fill="none" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M18 6L6 18M6 6l12 12" />
                    </svg>
                  </div>
                  <h3 className="info-request-modal__confirmation-heading">Something Went Wrong</h3>
                  <p className="info-request-modal__confirmation-text">
                    We couldn't submit your request. Please try again.
                  </p>
                  <button className="info-request-modal__submit" onClick={() => setSubmitStatus('idle')}>
                    Try Again
                  </button>
                </>
              )}
            </div>
          )}
        </div>
      </div>
    </>,
    document.body
  );
};

export default InfoRequestModal;
